import { useEffect, useRef, useState, type ComponentType, type PointerEvent, type ReactNode } from 'react';
import { CheckCircle, Info, WarningCircle, X } from '@phosphor-icons/react';
import type { IconProps } from '@phosphor-icons/react';
import { cx } from '../cx';
import { TONE_TEXT, type Tone } from '../tone';
import { Icon } from '../Icon';
import { IconButton } from '../IconButton/IconButton';

/** The shared status vocabulary (ui/tone.ts): positive for done, attention for failed (it stays until closed). */
export type ToastTone = Exclude<Tone, 'neutral'>;

const GLYPH: Record<ToastTone, ComponentType<IconProps>> = {
  positive: CheckCircle,
  info: Info,
  attention: WarningCircle,
};

export const TOAST_MIN_DURATION = 5000;

export type ToastProps = {
  tone?: ToastTone;
  title: ReactNode;
  body?: ReactNode;
  /** Optional action, typically an Undo button. */
  action?: ReactNode;
  onDismiss: () => void;
  /** Accessible name of the close button, localised by the caller. */
  dismissLabel: string;
  /** Milliseconds before it leaves: 6000 by default, and any value under 5000 is raised to 5000 so it can be read. Attention toasts stay until dismissed. */
  duration?: number;
  className?: string;
};

/** How far (px) a toast must be swiped sideways to dismiss it. */
export const TOAST_SWIPE_DISTANCE = 80;

/**
 * A short confirmation of what just happened, with an optional undo, that leaves on its own.
 * Success and info are polite status messages; errors are alerts and stay until closed. Hovering
 * or focusing it pauses the timer. Place toasts inside a ToastViewport.
 */
export function Toast({
  tone = 'positive',
  title,
  body,
  action,
  onDismiss,
  dismissLabel,
  duration = 6000,
  className,
}: ToastProps) {
  const [paused, setPaused] = useState(false);
  const remaining = useRef(Math.max(duration, TOAST_MIN_DURATION));
  const dismiss = useRef(onDismiss);
  dismiss.current = onDismiss;
  const persistent = tone === 'attention';

  useEffect(() => {
    if (persistent || paused) return undefined;
    const started = Date.now();
    const timer = setTimeout(() => dismiss.current(), remaining.current);
    return () => {
      clearTimeout(timer);
      remaining.current -= Date.now() - started;
    };
  }, [paused, persistent]);

  // Swipe sideways to dismiss (touch or mouse drag); a short drag snaps back.
  const [dragX, setDragX] = useState(0);
  const dragStart = useRef<number | null>(null);
  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest('button, a')) return;
    dragStart.current = e.clientX;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setPaused(true);
  };
  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    if (dragStart.current !== null) setDragX(e.clientX - dragStart.current);
  };
  const onPointerUp = () => {
    if (dragStart.current === null) return;
    dragStart.current = null;
    if (Math.abs(dragX) > TOAST_SWIPE_DISTANCE) dismiss.current();
    else setDragX(0);
    setPaused(false);
  };

  return (
    <div
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      style={
        dragX ? { transform: `translateX(${dragX}px)`, opacity: Math.max(0.3, 1 - Math.abs(dragX) / 240) } : undefined
      }
      role={persistent ? 'alert' : 'status'}
      aria-live={persistent ? 'assertive' : 'polite'}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      className={cx(
        'pointer-events-auto flex w-full max-w-sm touch-pan-y items-start gap-3 rounded-jc-container bg-jc-surface p-4 text-jc-ink shadow-jc-lg ring-1 ring-jc-card-border motion-safe:animate-jc-rise',
        className,
      )}
    >
      <span className={cx('mt-0.5 shrink-0', TONE_TEXT[tone])}>
        <Icon icon={GLYPH[tone]} size="md" weight="fill" />
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <p className="text-sm font-semibold">{title}</p>
        {body ? <div className="text-sm text-jc-ink-secondary">{body}</div> : null}
      </div>
      {action ? <div className="shrink-0 self-center">{action}</div> : null}
      <IconButton icon={X} label={dismissLabel} variant="ghost" size="sm" onClick={onDismiss} />
    </div>
  );
}

/** Where toasts stack: bottom centre on phones, bottom right from tablet, above sheets and dialogs. */
export function ToastViewport({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cx(
        'pointer-events-none fixed inset-x-0 bottom-0 z-(--jc-z-toast) flex flex-col items-center gap-2 p-4 md:inset-x-auto md:right-0 md:items-end',
        className,
      )}
    >
      {children}
    </div>
  );
}
